import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey, red } from '@mui/material/colors';
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, isEmpty, isEqual, isNull, isUndefined } from "lodash";

import mrpReportService from "@plannel/services/mp/mrp-report-service";
import planVersionService from "@plannel/services/data/plan-version-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"

import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import { FS } from "@plannel/utils/font-util";

const { mpMeasure, versionStatusCode, module } = CodeUtil;
const { RELEASED, PLANNED } = versionStatusCode;
const { MP } = module;
const { GROSS_REQ, SCHEDULED_RECEIPTS, PROJ_ON_HAND, EOH, NET_REQ, PO_RECEIPTS, PO_RELEASES, TARGET_INV_QTY } = mpMeasure;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const { snackBarMessageColor } = ColorUtil;
const MrpReport = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [30, 50, 100];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(reduxViewState?.version ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용

  const gridRef = useRef();
  const columnDefs = useRef();
  const bucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(() => [GROSS_REQ, SCHEDULED_RECEIPTS, TARGET_INV_QTY, PROJ_ON_HAND, EOH, NET_REQ, PO_RECEIPTS, PO_RELEASES], []);
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);

  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);

  const defaultOptions = { barSize: 20, strokeWidth: 1.5 };
  const chartSeries = [
    { field: "grossReq", text: t("grossReq"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(0), defaultOptions }, groupName: t("qty") },
    { field: "scheduledReceipts", text: t("scheduledReceipts"), type: "line", typeOptions: { fill: ColorUtil.colorSet(1), defaultOptions }, groupName: t("qty") },
    { field: "projOnhand", text: t("projOnhand"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(2), defaultOptions }, groupName: t("qty") },
    { field: "eoh", text: t("eoh"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(6), defaultOptions }, groupName: t("qty") },
    { field: "netReq", text: t("netReq"), type: "bar", typeOptions: { stroke: ColorUtil.colorSet(3), defaultOptions }, groupName: t("qty") },
    { field: "poReceipts", text: t("poReceipts"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(4), defaultOptions }, groupName: t("qty") },
    { field: "poReleases", text: t("poReleases"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(5), defaultOptions }, groupName: t("qty") },
  ];

  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    xAxisSortMap: gridHeaderMap.current
  };

  const getRowStyle = params => {
    if (params?.data?.measure && isEqual(params.data.measure, GROSS_REQ)) {
      return { background: green[50] };
    }

    return null;
  }

  const emptyValueFormatter = params => {
    if (!params.node.data) {
      return ""
    }
    if (isNull(params.value) || isUndefined(params.value)) {
      return 0;
    }
    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const defaultColumnDefs = [
    {
      headerName: 'locCd', colId: "locCdGrp", width: 140, showRowGroup: 'locCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'locName', colId: "locNameGrp", width: 160, showRowGroup: 'locName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 140, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true },
    },
    {
      headerName: 'onhandInv', colId: "onhandInvQtyGrp", width: 160, showRowGroup: 'onhandInvQty', pinned: "left", lockPinned: true,
      type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locName", field: "locName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemName", field: "itemName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "onhandInv", field: "onhandInvQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
      valueFormatter: params => t(params.value), headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      }
    }
  ];

  const defaultExportColumnDefs = [
    { field: "locCd", colId: "locCdGrp", rowGroup: true, hide: true },
    { field: "locName", colId: "locNameGrp", rowGroup: true, hide: true },
    { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
    { field: "itemName", colId: "itemNmGrp", rowGroup: true, hide: true },
    { field: "onhandInv", colId: "onhandInvQtyGrp", rowGroup: true, hide: true },
    { headerName: "locCd", field: "locCd", width: 140, cellClass: 'stringType' },
    { headerName: "locName", field: "locName", width: 140 },
    { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", width: 140 },
    { headerName: "onhandInv", field: "onhandInvQty", width: 140 },
    { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
  ];

  const sortMpVersions = versions => {
    const basicVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        basicVersions.push(ver);
      }
    }

    return [...basicVersions, ...releaseVersions];
  };

  const retrieveMpVersion = receivedVersion => {
    planVersionService.getAll({ moduleCd: MP, statusCds: [PLANNED, RELEASED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          const sortedVersions = sortMpVersions(results);
          setVersions(sortedVersions);

          if (receivedVersion) {
            const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
            currentVersion ? setVersion(currentVersion) : setVersion(results[0]);
          } else {
            if (reduxViewState?.version) {
              const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
              if (isUndefined(reduxVersion)) {
                setVersion(sortedVersions[0]);
              }
            } else {
              setVersion(sortedVersions[0]);
            }
          }
        } else {
          setVersions([]);
          setVersion(null);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (version) {
      params.versionId = version.id;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    return { ...params, ...sortParams };
  }

  const setGridHeaders = (headers, headerMap) => {
    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = version?.id ? GridUtils.IGNORE_P13N_COLLID + version.id : GridUtils.IGNORE_P13N_COLLID;
    const colDefs = cloneDeep(defaultColumnDefs);
    bucketColumnDefs.current = []

    headers.forEach((header) => {
      bucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["nonEditableColumn", "rightAligned", "numberColumn"],
        valueFormatter: emptyValueFormatter
      });
    });

    columnDefs.current = [...colDefs, ...bucketColumnDefs.current];
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...colDefs, ...bucketColumnDefs.current] });
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const grossReqQtyPivotData = "grossReqQtyPivotData";
    const scheduledReceiptsPivotData = "scheduledReceiptsPivotData";
    const projOnhandQtyPivotData = "projOnhandQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const netReqQtyPivotData = "netReqQtyPivotData";
    const poReceiptsQtyPivotData = "poReceiptsQtyPivotData";
    const poReleasesQtyPivotData = "poReleasesQtyPivotData";
    const targetInvQtyPivotData = "targetInvQtyPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const data = { ...resultsData, measure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case GROSS_REQ:
              data[header] = resultsData[grossReqQtyPivotData][idx];
              break;

            case SCHEDULED_RECEIPTS:
              data[header] = resultsData[scheduledReceiptsPivotData][idx];
              break;

            case TARGET_INV_QTY:
              data[header] = resultsData[targetInvQtyPivotData][idx];
              break;

            case PROJ_ON_HAND:
              data[header] = resultsData[projOnhandQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case NET_REQ:
              data[header] = resultsData[netReqQtyPivotData][idx];
              break;

            case PO_RECEIPTS:
              data[header] = resultsData[poReceiptsQtyPivotData][idx];
              break;

            case PO_RELEASES:
              data[header] = resultsData[poReleasesQtyPivotData][idx];
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrieveMrpReport = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mrpReportService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);

          const datas = setGridDatas(localDateHeader, data);
          setData(datas);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          columnDefs.current = defaultColumnDefs;
          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
          setHeaders([]);
          setData([]);
          setChartData([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { version, filter: filterValue, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });

    retrieveMpVersion();
  }
  
  const setChart = (selectedRows) => {
    const selectedDatas = [];
    const dateFormat = [];

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    for (const header of headers) {
      dateFormat.push(gridHeaderMap.current[header]);
    }

    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;

      for (const header of headers) {
        const series = [GROSS_REQ, SCHEDULED_RECEIPTS, TARGET_INV_QTY, PROJ_ON_HAND, EOH, NET_REQ, PO_RECEIPTS, PO_RELEASES];
        if (series.includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          chartDatas[headerName] = row[header];
          chartDatas.measure = t(row.measure);

          inputFlag = true;
        } else {
          break;
        }
      }

      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(dateFormat);
    setChartData(selectedDatas);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
      setChartData([]);
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
      }
    }
  }

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMrpReport();
    }
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iconColor = planVersion => {
    if (planVersion) {
      const { statusCd } = planVersion;
      if (isEqual(statusCd, RELEASED)) {
        return grey[900];
      } else if (isEqual(statusCd, PLANNED)) {
        return green[600];
      }

      return red[600];
    }

    return null;
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading && version && !isEmpty(measures)) {
      retrieveMrpReport();
    }

    return () => {
      setData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams]);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, viewName]);

  useEffect(() => {
    if (gridLoading && version && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, filterValue]);

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={versions}
          value={version}
          onChange={(_, newValue) => setVersion(newValue)}
          label={t("version")}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          minWidth={250}
          inputIconSx={{ ml: 1 }}
        />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} styles={{ width: 250 }} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={mrpReportService} setExcelData={setGridDatas} columnDefs={[...defaultExportColumnDefs, ...bucketColumnDefs.current]}
            totalRows={resultTotalCount.current * rowsPerCombination} params={{ ...getRequestParams(false), measureCount: rowsPerCombination }} />
        </Box>
      </FilterContainer>
      <SplitterContainer>
        <Box sx={{ height: "100%" }}>
          <Box id="mrp-report-grid" className="ag-theme-balham grid-area non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
            <AgGridReact
              ref={gridRef}
              rowData={data}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                groupSelects: "descendants",
              }}
              groupDisplayType={'custom'}
              groupDefaultExpanded={-1}
              groupHideOpenParents
              statusBar={statusBar}
              getRowStyle={getRowStyle}
              onRowSelected={onRowSelected}
              onCellClicked={onCellGridClicked}
              onRowDataUpdated={initCheck}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            >
            </AgGridReact>
          </Box>
          <PaginationContainer
            currentPage={currentPage} totalPages={gridPages}
            pageSize={pageSize} pageSizes={pageSizes}
            setCurrentPage={setCurrentPage} setPageSize={setPageSize}
          />
        </Box>
        <Box id="mrp-report-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
          <PivotChartComposed
            pivotConfig={pivotConfig}
            data={chartData}
            options={{ tooltipContentStyle: { fontSize: FS.SUB } }}
            nullConvert
            activeMeasures={!isEmpty(measures) ? measures : allMeasure}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
      />
    </>
  );
};

export default withTranslation()(MrpReport);

